import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  IonContent,
  IonList,
  IonItem,
  IonInput,
  IonLabel,
  IonButton,
  IonSelect,
  IonSelectOption,
  IonDatetimeButton,
  IonDatetime,
  IonModal,
  IonLoading,
  IonToast,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonTitle,
  useIonAlert,
  IonText,
  IonIcon,
  IonFooter,
  IonRadioGroup,
  IonRadio,
  useIonToast,
} from "@ionic/react";
import {
  Employee,
  getAllFields,
  LaborResults,
} from "../models/workorders/LaborActivity";
import { decodeParam } from "../util/ApiHelper";
import { useParams } from "react-router";
import FilterModal from "../components/FilterModal";
import moment from "moment";
import OnlineStatus from "../components/OnlineStatus";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../util/AppInsights";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import { getLaborsByWorkOrder } from "../api/Labors";
import { getEmployeesLiteList, findEmployee } from "../api/Employees";
import { getEmployeesAccess, getUserId } from "../util/UserHelper";
import { ApiResponse } from "../models/api/ApiResponse";
import { getValueSearchDetails } from "../api/QueryDesigner";
import {
  searchOutline,
  searchSharp,
  chevronDownOutline,
  filterCircleOutline,
  filterCircleSharp,
} from "ionicons/icons";
import ISSPopover from "../components/ISSPopover";
import { logCustomMessage } from "../util/logger";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { useUserStore } from "../store/user";
import { useOfflineStore } from "../store/offline-mode";
import useGetTimeEntryMetaData from "../hooks/work-order/time-entry/useGetTimeEntryMetaData";
import useGetCraftsForEmployee from "../hooks/work-order/time-entry/useGetCraftsForEmployee";
import { useShallow } from "zustand/react/shallow";
import useAddUpdateTimeEntry from "../hooks/work-order/time-entry/useAddUpdateTimeEntry";
import { useWoOfflineUpdatesStore } from "../store/wo-offline-updates";
import useGetRateList from "../hooks/work-order/time-entry/useGetRateList";
import { useWorkOrderStore } from "../store/wo-order";
import useGetLaborLogs from "../hooks/work-order/useGetLaborLogs";

interface TimeEntryModalProps {
  isOpen: boolean;
  cancel: () => void;
  confirm: (timeEntry: LaborResults[]) => void;
  finalTime?: number;
  objectID?: string;
  pathId?: string;
  workOrder?: string;
  offlineTimeEntryId?: string;
}

interface ValidValues {
  Code: string;
  Description: string;
}

const TimeEntry: React.FC<TimeEntryModalProps> = (props) => {
  const {
    isOpen,
    cancel,
    confirm,
    finalTime,
    objectID,
    pathId,
    workOrder,
    offlineTimeEntryId,
  } = props;

  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { workOrderId } = useParams<{ workOrderId: string }>();
  let displayWo = decodeParam(workOrderId);
  const [presentAlert] = useIonAlert();
  const [orgLaborLog, setOrgLaborLog] = useState<LaborResults[]>([]);
  let [currentEmployee, setCurrentEmployee] = useState<string>("");
  const [loggedInEmployee, setLoggedInEmployee] = useState<string>(
    useUserStore.getState().loggedInEmployee?.EmployeeId ?? ""
  );
  const [employeeList, setEmployeeList] = useState<Employee[]>([]);
  const [hasEmployeeAccess, setHasEmployeeAccess] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [laborLogErrorState, setLaborLogErrorState] = useState(false);
  const [metadataErrorState, setMetadataErrorState] = useState(false);
  const [craftListErrorState, setCraftListErrorState] = useState(false);
  const [rateListErrorState, setRateListErrorState] = useState(false);
  const [currentDate, setCurrentDate] = useState<string>();
  const [currentCraft, setCurrentCraft] = useState("");
  const [occurrence, setOccurrence] = useState<number>();
  const [hours, setHours] = useState<number>();
  const [currentRate, setCurrentRate] = useState("");
  const [userComments, setUserComments] = useState<string>("");
  const [timeEntry, setTimeEntry] = useState<LaborResults[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [woBase, setWoBase] = useState("");

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterModalColumnName, setFilterModalColumnName] = useState("");
  const [filterModalModuleName, setFilterModalModuleName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [, setEmployeeValidList] = useState<ValidValues[]>([]);
  const popover = useRef<HTMLIonPopoverElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const requiredFieldsMap = useRef<{ [key: string]: boolean }>({}); // Hashmap to store required fields
  const [present] = useIonToast();

  const isOffline = useOfflineStore((state) => state.isOffline);
  const metadata = useWorkOrderStore((state) => state.timeEntryMetaData);
  const craftList = useWorkOrderStore(useShallow((state) => state.crafts));
  const addNewTimeEntry = useWoOfflineUpdatesStore(
    (state) => state.addNewTimeEntry
  );
  const offlineTimeEntry = useWoOfflineUpdatesStore((state) =>
    state.getTimeEntryById(offlineTimeEntryId ?? "")
  );
  const rateList = useWorkOrderStore(useShallow((state) => state.rateList));
  const { getTimeEntryMetaData } = useGetTimeEntryMetaData();
  const { getCraftsForEmployee } = useGetCraftsForEmployee();
  const { addUpdateTimeEntry } = useAddUpdateTimeEntry();
  const { getRateList: getValidations } = useGetRateList();
  const { getLaborLogs } = useGetLaborLogs();

  const getEmployee = useCallback(async () => {
    if (loggedInEmployee) return; //got it from the cache
    await getUserId().then((response) => {
      logCustomMessage(
        "Get userId for time entry..",
        SeverityLevel.Information,
        undefined,
        undefined,
        response
      );
      if (response !== undefined) {
        setLoggedInEmployee(response!);
      }
    });
  }, [loggedInEmployee]);

  const onCancel = () => {
    cancel();
    if (objectID) {
      getLaborLog();
    } else {
      if (finalTime) {
        setHours(finalTime);
      } else {
        setHours(undefined);
      }
      resetInputs();
    }
  };

  const metadataQuery = useCallback(async () => {
    const updateRequiredFields = (fields: any) => {
      Object.values(fields).forEach((field: any) => {
        const propertyName = field.PropertyName;
        if (propertyName) {
          requiredFieldsMap.current[propertyName] = field.IsRequired;
        }
      });
    };

    if (metadata) {
      setEmployeeValidList(
        metadata?.fields["wo_log_labor.employee_id"]?.ValidValues
      );
      updateRequiredFields(metadata.fields);
      return;
    }

    setShowLoading(true);
    const response = await getTimeEntryMetaData();
    if (response.status !== 200) {
      setMetadataErrorState(true);
    }
    updateRequiredFields(response.data.fields);
    setShowLoading(false);
  }, [getTimeEntryMetaData, metadata]);

  const checkUserAccess = useCallback(async () => {
    if (await getEmployeesAccess()) {
      getAllEmployeeIds();
      setHasEmployeeAccess(true);
    } else {
      setHasEmployeeAccess(false);
    }
  }, []);

  const getAllEmployeeIds = async () => {
    const data = {
      PropertyList: ["EmployeeName", "EmployeeId"],
      Filter: "",
    };
    getEmployeesLiteList(data).then((response) => {
      logCustomMessage(
        "Get all employee IDs",
        SeverityLevel.Information,
        "Employees/GetLiteList",
        data,
        response
      );
      setEmployeeList(response.data);
    });
  };

  const getCraftList = useCallback(
    async (employee: string) => {
      if (craftList) {
        if (!objectID) {
          setCurrentCraft(
            offlineTimeEntry?.RequestObject.ResourceId ??
              craftList?.PrimaryCraftCode
          );
        }
        return; //got it from the cache
      }
      const response = await getCraftsForEmployee(employee);
      if (response.status !== 200) {
        setCraftListErrorState(true);
      } else if (!objectID) {
        setCurrentCraft(
          offlineTimeEntry?.RequestObject.ResourceId ??
            response.data?.PrimaryCraftCode
        );
      }
    },
    [
      craftList,
      getCraftsForEmployee,
      objectID,
      offlineTimeEntry?.RequestObject.ResourceId,
    ]
  );

  const handleSelectedEmployee = useCallback(
    async (employee: string) => {
      setCurrentEmployee(employee);
      getCraftList(employee);
    },
    [getCraftList]
  );

  const getRateList = useCallback(async () => {
    let validationCodes = rateList;
    if (!validationCodes) {
      //if it is not there in the cache
      const response = await getValidations();
      if (response.status !== 200) {
        setRateListErrorState(true);
      } else {
        validationCodes = response.data;
      }
    }

    if (!objectID) {
      let splitRate = validationCodes?.at(0)?.Description.split(" ");
      let rateDescription = splitRate ? splitRate[splitRate.length - 1] : "";
      setCurrentRate(
        offlineTimeEntry?.RequestObject.RateName ?? rateDescription
      );
    }
  }, [
    getValidations,
    objectID,
    offlineTimeEntry?.RequestObject.RateName,
    rateList,
  ]);

  const resetInputs = useCallback(async () => {
    setCurrentEmployee(
      offlineTimeEntry?.RequestObject.EmployeeId ?? loggedInEmployee
    );
    getCraftList(loggedInEmployee);
    getRateList();
    setOccurrence(undefined);
    setUserComments(offlineTimeEntry?.RequestObject.Comments ?? "");
    setWoBase("");
    const date = offlineTimeEntry
      ? new Date(offlineTimeEntry.RequestObject.WorkDate)
      : new Date();
    setCurrentDate(moment(date).format("YYYY-MM-DDTHH:mm:ssZ"));
  }, [getCraftList, getRateList, loggedInEmployee, offlineTimeEntry]);

  const getLaborLog = useCallback(async () => {
    await getLaborsByWorkOrder({
      woNumber: displayWo,
      appName: "Mobile",
      translations: true,
    }).then((response) => {
      const data = {
        woNumber: displayWo,
        appName: "Mobile",
        translations: true,
      };
      logCustomMessage(
        "Get all Labor Logs",
        SeverityLevel.Information,
        "Labors/ByWorkOrder",
        data,
        response
      );
      if (response.status !== 200) {
        setLaborLogErrorState(true);
      } else {
        let correctLog = response.data.find(
          (log: { ObjectId: string | undefined }) => {
            return log.ObjectId === objectID;
          }
        );
        if (correctLog) {
          setOrgLaborLog(correctLog);
          setCurrentDate(correctLog.WorkDate);
          handleSelectedEmployee(correctLog.EmployeeId);
          getRateList();
          setCurrentCraft(correctLog.ResourceId);
          setCurrentRate(correctLog.RateName);
          setOccurrence(correctLog.Occurrence);
          setHours(correctLog.WorkHours);
          setUserComments(correctLog.Comments);
        }
      }
    });
  }, [displayWo, getRateList, handleSelectedEmployee, objectID]);

  const setupTimeEntryDetails = useCallback(async () => {
    await getEmployee();
    if (!isOffline) await checkUserAccess();
    await metadataQuery();

    if (objectID) {
      getLaborLog();
    } else {
      loggedInEmployee && (await resetInputs());
      if (finalTime) {
        setHours(finalTime);
      } else {
        setHours(undefined);
      }
    }
  }, [
    getEmployee,
    isOffline,
    checkUserAccess,
    metadataQuery,
    objectID,
    getLaborLog,
    loggedInEmployee,
    finalTime,
  ]);

  useEffect(() => {
    if (!isOpen) return; //not need to setup as the time entry dialog is not opened
    setupTimeEntryDetails();
  }, [isOpen, setupTimeEntryDetails]);

  const filteredEmployeeList = useMemo(() => {
    if (!Array.isArray(employeeList) || employeeList.length === 0) {
      return [];
    }

    if (!filter.trim()) {
      return employeeList;
    }

    return employeeList.filter((card) => {
      const employeeId = card?.Properties?.EmployeeId?.toLowerCase() || "";
      const employeeName = card?.Properties?.EmployeeName?.toLowerCase() || "";
      const filterLower = filter.toLowerCase();

      return (
        employeeId.includes(filterLower) || employeeName.includes(filterLower)
      );
    });
  }, [employeeList, filter]);

  async function saveTimeEntry() {
    const labors: LaborResults[] = [];
    if (displayWo === "") {
      displayWo =
        isOffline || offlineTimeEntryId ? workOrder ?? woBase : woBase;
    }
    if (displayWo === "") {
      presentAlert({
        header: "Error",
        message:
          translatedMessages["EnterValidWorkOrder"]?.MessageText ||
          "Please enter a valid Work Order.",
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
          },
        ],
      });
      return;
    }

    const requiredFields = [
      { key: "WorkDate", value: currentDate },
      { key: "WorkHours", value: hours },
      { key: "ResourceId", value: currentCraft },
      { key: "EmployeeId", value: currentEmployee },
      { key: "RateName", value: currentRate },
      { key: "Comments", value: userComments },
      { key: "Occurrence", value: occurrence },
    ];

    for (const field of requiredFields) {
      if (
        requiredFieldsMap.current[field.key] &&
        (field.value === undefined ||
          field.value === null ||
          (typeof field.value === "string" && field.value.trim() === ""))
      ) {
        present({
          message: translatedMessages["errRequired"]?.MessageText
            ? translatedMessages["errRequired"].MessageText.replace(
                "@1",
                field.key
              )
            : `${field.key} is required.`,
          duration: 3000,
          position: "top",
          color: "danger",
        });
        return;
      }
    }

    let multiplierVal = 1;
    const currentRateItem = (rateList ?? []).find((item: any) => {
      const descriptionParts = item.Description.split(" - ");
      return descriptionParts[1] === currentRate;
    });
    if (currentRateItem) {
      multiplierVal = Number.parseFloat(currentRateItem.Code);
    }
    if (objectID) {
      const data = {
        RequestObject: {
          ...orgLaborLog,
          WorkDate: moment(currentDate).format("MM/DD/YYYY"),
          WorkHours: hours,
          ResourceId: currentCraft,
          RateName: currentRate,
          Occurrence: occurrence,
          EnteredBy: loggedInEmployee,
          Comments: userComments,
          EmployeeId: currentEmployee,
          CfWorkOrder: displayWo,
          Multiplier: multiplierVal,
        },
        Confirmation: {},
      };

      const response = await addUpdateTimeEntry(data, true);

      labors.push(data.RequestObject);
      setTimeEntry(labors);
      if (response.data.Success) {
        // Display Success modal & then redirect
        presentAlert({
          header: `${
            translatedMessages["ActivitySaveSuccess"]?.MessageText ||
            "Log Activity Saved Successfully."
          }`,
          buttons: [
            {
              text: translations["lbl_btn_ok"] || "OK",
              handler: () => {
                confirm(timeEntry);
              },
            },
          ],
        });
        if (data.RequestObject.Comments) {
          const logsResponse = await getLaborLogs(displayWo);
          if (logsResponse.status === 200 && logsResponse.data) {
            useWorkOrderStore
              .getState()
              .setWoLaborLog(displayWo, logsResponse.data);
          }
        }
      } else {
        // Display failure modal and stay on the page
        presentAlert({
          header: "Editing failed",
          buttons: [
            {
              text: translations["lbl_btn_ok"] || "OK",
            },
          ],
        });
      }
    } else {
      const data = {
        RequestObject: {
          WorkDate: moment(currentDate).format("MM/DD/YYYY"),
          WorkHours: hours,
          ResourceId: currentCraft,
          Multiplier: multiplierVal,
          WorkOrderNumber: displayWo,
          Occurrence: occurrence,
          Comments: userComments,
          EmployeeId: currentEmployee,
          RateName: currentRate,
        },
        Confirmation: {},
      };
      if (isOffline && !offlineTimeEntryId) {
        addNewTimeEntry({ ...data, woNumber: displayWo });
        presentAlert({
          message: `${
            translatedMessages["MobOfflineTimeSaveSuccess"]?.MessageText ||
            "Log Activity added in offline store."
          }`,
          buttons: [
            {
              text: translations["lbl_btn_ok"] || "OK",
              handler: () => {
                confirm(timeEntry);
              },
            },
          ],
        });
        return; //we will sync it once user is online
      }
      const response = await addUpdateTimeEntry(data, false);
      labors.push(data.RequestObject);
      setTimeEntry(labors);
      if (response.data.Success !== true) {
        setShowErrorToast(true);
        setErrorMessage(response.data?.MessageList[0]?.Text);
      } else {
        const logsResponse = await getLaborLogs(displayWo);
        if (logsResponse.status === 200 && logsResponse.data) {
          useWorkOrderStore
            .getState()
            .setWoLaborLog(displayWo, logsResponse.data);
        }
        confirm(timeEntry);
        resetInputs();
        if (finalTime) {
          setHours(finalTime);
        } else {
          setHours(undefined);
        }
      }
    }
  }
  const resetSearchState = () => {
    setFilterModalColumnName("");
    setFilterModalModuleName("");
  };

  const onFilterModalConfirm = (
    selectedItem: string | number | null | undefined
  ) => {
    setParameter(filterModalColumnName, selectedItem);
    setIsFilterModalOpen(false);
    resetSearchState();
  };

  const setParameter = (
    paramKey: string,
    paramValue: string | number | null | undefined
  ) => {
    if (paramKey === "cf_work_order") {
      setWoBase(paramValue as string);
    } else if (paramKey === "employee_id") {
      setCurrentEmployee(paramValue as string);
    }
  };

  const errorDisplay = () => {
    let errorState = "";
    switch (true) {
      case metadataErrorState:
        errorState =
          translatedMessages["ErrFetchingLabor"]?.MessageText ||
          "Error fetching the labor activity fields";
        break;
      case craftListErrorState:
        errorState =
          translatedMessages["ErrFetchingCraftList"]?.MessageText ||
          "Error fetching the craft list";
        break;
      case rateListErrorState:
        errorState =
          translatedMessages["ErrFetchingTheRateList"]?.MessageText ||
          "Error fetching the rate list";
        break;
      case laborLogErrorState || (objectID && !orgLaborLog && !showLoading):
        errorState =
          translatedMessages["ErrorReadingTimeEntries"]?.MessageText ||
          "Error reading time entries";
        break;
      default:
        break;
    }
    if (errorState !== "") {
      return <IonText color="danger">{errorState}</IonText>;
    }
  };

  const handleRadioChange = (e: any) => {
    setSelectedEmployee(e.detail.value);
  };

  const handleOk = () => {
    setCurrentEmployee(selectedEmployee);
    setShowModal(false);
  };

  const handleClose = () => {
    setSelectedEmployee(currentEmployee); // Reset selected reviewer
    setShowModal(false);
  };

  const handleEmployeeSearch = (e: CustomEvent) => {
    let newVal = (e.target as HTMLInputElement)?.value;
    setCurrentEmployee(newVal);
    if (newVal.length > 2) {
      findEmployee(newVal).then((response) => {
        const data = {
          Search: newVal,
        };
        logCustomMessage(
          "Finding employees with typeAhead",
          SeverityLevel.Information,
          "Employees/Find",
          data,
          response
        );
        if (response.status === 200) {
          if (Object.keys(response.data).length !== 0) {
            popover.current!.event = e;
            setPopoverOpen(true);
          } else if (popoverOpen) {
            setPopoverOpen(false);
          }
          let empSearchArray = [];
          const employees = Object.entries(response.data).map(([id, name]) => ({
            EmployeeId: id,
            EmployeeName: name,
          }));
          for (let search of employees) {
            empSearchArray.push(search.EmployeeId);
          }
          setSearchResults(empSearchArray);
        }
      });
    } else {
      if (popoverOpen) {
        setPopoverOpen(false);
      }
      if (searchResults.length > 0) {
        setSearchResults([]);
      }
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onCancel}>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />
      <IonToast
        isOpen={showErrorToast}
        onDidDismiss={() => setShowErrorToast(false)}
        message={errorMessage}
        duration={3000}
        position="top"
      />
      <FilterModal
        isOpen={isFilterModalOpen}
        moduleName={filterModalModuleName}
        cancel={() => setIsFilterModalOpen(false)}
        confirm={onFilterModalConfirm}
      />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onCancel}>
              {translations["lbl_btn_cancel"] || "Cancel"}
            </IonButton>
          </IonButtons>
          {!!objectID && !!orgLaborLog ? (
            <IonTitle>
              {translatedMessages["EditTimeEntry"]?.MessageText ||
                "Edit Time Entry"}
            </IonTitle>
          ) : (
            <IonTitle>
              {pathId === "FromTimeEntryMenu"
                ? translations["lbl_time_entry"] || "Time Entry"
                : `${displayWo || workOrder} - ${
                    translations["lbl_time_entry"] || "Time Entry"
                  }`}
            </IonTitle>
          )}
          <IonButtons slot="end">
            <IonButton disabled={!hours} onClick={() => saveTimeEntry()}>
              {translations["lbl_btn_save"] || "Save"}
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <OnlineStatus />
      </IonHeader>
      <IonContent fullscreen>
        {!!metadata && (
          <IonList lines="full">
            {getAllFields(metadata).map((field) => {
              if (
                pathId !== "FromTimeEntryMenu" &&
                field.PropertyName === "WoBase"
              )
                return null;
              return (
                <IonItem key={field.IdText}>
                  <IonLabel>{field.TranslatedIdText}</IonLabel>

                  {field.PropertyName === "WoBase" &&
                  pathId === "FromTimeEntryMenu" ? (
                    <>
                      <IonInput
                        class="ion-text-end"
                        value={woBase}
                        onIonInput={(ev) =>
                          onFilterModalConfirm(ev.target.value)
                        }
                      ></IonInput>
                      <IonIcon
                        slot="end"
                        ios={searchOutline}
                        md={searchSharp}
                        onClick={async () => {
                          resetSearchState();
                          setFilterModalColumnName("cf_work_order");
                          const response: ApiResponse =
                            await getValueSearchDetails({
                              tableName: "wo_work_order",
                              columnName: "cf_work_order",
                            });
                          if (response.status === 200) {
                            setFilterModalModuleName(
                              response.data.QueryWizardModuleName
                            );
                            setIsFilterModalOpen(true);
                          }
                        }}
                      ></IonIcon>
                    </>
                  ) : field.PropertyName === "WorkDate" ? (
                    <>
                      <IonDatetimeButton datetime="datetime"></IonDatetimeButton>
                      <IonModal keepContentsMounted={true}>
                        <IonDatetime
                          id="datetime"
                          presentation="date"
                          value={currentDate}
                          onIonChange={(ev) =>
                            setCurrentDate(String(ev.detail.value))
                          }
                        ></IonDatetime>
                      </IonModal>
                    </>
                  ) : field.PropertyName === "EmployeeId" ? (
                    field.ElementType === "Dropdown" ? (
                      <React.Fragment>
                        <IonItem onClick={() => setShowModal(true)}>
                          <IonLabel slot="end">{currentEmployee}</IonLabel>
                          <IonIcon icon={chevronDownOutline} slot="end" />
                        </IonItem>

                        <IonModal isOpen={showModal}>
                          <IonHeader>
                            <IonToolbar>
                              <IonTitle>Select Employee</IonTitle>
                              <IonButtons slot="end">
                                <IonButton onClick={handleClose}>
                                  Close
                                </IonButton>
                              </IonButtons>
                            </IonToolbar>
                          </IonHeader>
                          <IonContent>
                            <IonItem>
                              <IonInput
                                placeholder="Filter Employees"
                                onIonChange={(e) => setFilter(e.detail.value!)}
                                value={filter}
                              ></IonInput>
                              <IonIcon
                                slot="end"
                                ios={filterCircleOutline}
                                md={filterCircleSharp}
                              ></IonIcon>
                            </IonItem>
                            <IonRadioGroup
                              value={selectedEmployee}
                              onIonChange={handleRadioChange}
                            >
                              {hasEmployeeAccess &&
                                filteredEmployeeList.map((employee, index) => (
                                  <IonItem key={index}>
                                    <IonLabel>
                                      {employee.Properties.EmployeeId} -{" "}
                                      {employee.Properties.EmployeeName}
                                    </IonLabel>
                                    <IonRadio
                                      slot="start"
                                      value={employee.EmployeeId}
                                    />
                                  </IonItem>
                                ))}
                              {!hasEmployeeAccess && (
                                <IonRadio>{currentEmployee}</IonRadio>
                              )}
                            </IonRadioGroup>
                          </IonContent>
                          <IonFooter>
                            <IonButton expand="block" onClick={handleOk}>
                              OK
                            </IonButton>
                            <IonButton
                              fill="outline"
                              expand="block"
                              onClick={handleClose}
                            >
                              Cancel
                            </IonButton>
                          </IonFooter>
                        </IonModal>
                      </React.Fragment>
                    ) : field.ElementType === "ISS" ? (
                      <>
                        <IonInput
                          class="ion-text-end"
                          value={currentEmployee}
                          disabled={!!objectID}
                          onIonInput={(ev) => {
                            onFilterModalConfirm(ev.target.value);
                            handleEmployeeSearch(ev);
                          }}
                        ></IonInput>
                        <IonIcon
                          slot="end"
                          ios={searchOutline}
                          md={searchSharp}
                          onClick={async () => {
                            resetSearchState();
                            setFilterModalColumnName("employee_id");
                            const response: ApiResponse =
                              await getValueSearchDetails({
                                tableName: "em_employee",
                                columnName: "employee_id",
                              });
                            const data = {
                              tableName: "em_employee",
                              columnName: "employee_id",
                            };
                            logCustomMessage(
                              "Showing query wizard",
                              SeverityLevel.Information,
                              "QueryDesigner/GetValueSearchDetails",
                              data,
                              response
                            );
                            if (response.status === 200) {
                              setFilterModalModuleName(
                                response.data.QueryWizardModuleName
                              );
                              setIsFilterModalOpen(true);
                            }
                          }}
                        ></IonIcon>
                      </>
                    ) : null
                  ) : field.PropertyName === "ResourceId" ? (
                    <IonSelect
                      slot="end"
                      interface="popover"
                      placeholder={translations["lbl_btn_select"] || "Select"}
                      value={currentCraft}
                      disabled={!!objectID}
                      onIonChange={(ev) => setCurrentCraft(ev.detail.value)}
                    >
                      {(craftList?.Crafts ?? []).map(
                        (craft: any, index: any) => {
                          return (
                            <IonSelectOption key={`${index}-${craft}`}>
                              {craft.Code}
                            </IonSelectOption>
                          );
                        }
                      )}
                    </IonSelect>
                  ) : field.PropertyName === "Occurrence" ? (
                    <IonInput
                      class="ion-text-end"
                      inputMode="decimal"
                      value={occurrence}
                      required
                      type="number"
                      onIonInput={(ev) =>
                        setOccurrence(Number(ev.target.value))
                      }
                    ></IonInput>
                  ) : field.PropertyName === "WorkHours" ? (
                    <IonInput
                      class="ion-text-end"
                      inputMode="decimal"
                      value={hours}
                      required
                      type="number"
                      onIonInput={(ev) => {
                        setHours(Number(ev.target.value));
                      }}
                    ></IonInput>
                  ) : field.PropertyName === "RateName" ? (
                    <IonSelect
                      slot="end"
                      interface="popover"
                      placeholder={translations["lbl_btn_select"] || "Select"}
                      value={currentRate}
                      onIonChange={(ev) => setCurrentRate(ev.detail.value)}
                    >
                      {(rateList ?? []).map((rate: any, index: any) => {
                        let splitRate = rate.Description.split(" - ");
                        let rateDescription = splitRate[1];
                        return (
                          <IonSelectOption key={`${index}-${rate}`}>
                            {rateDescription}
                          </IonSelectOption>
                        );
                      })}
                    </IonSelect>
                  ) : field.PropertyName === "Comments" ? (
                    <IonInput
                      class="ion-text-end"
                      value={userComments}
                      onIonInput={(ev) => {
                        setUserComments(String(ev.target.value));
                      }}
                    ></IonInput>
                  ) : (
                    <IonInput class="ion-text-end"></IonInput>
                  )}
                </IonItem>
              );
            })}
          </IonList>
        )}
        {objectID && !orgLaborLog && !showLoading ? errorDisplay() : <></>}
      </IonContent>
      <ISSPopover
        popover={popover}
        popoverOpen={popoverOpen}
        setPopoverOpen={setPopoverOpen}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        setElementValue={setCurrentEmployee}
      />
    </IonModal>
  );
};

export default withAITracking(reactPlugin, TimeEntry, "TimeEntry");
